
import React, { useState, useRef, useEffect, useCallback } from 'react';
import './TerminalPage.css';
import PageHeader from '../../components/shared/PageHeader';
import Button from '../../components/shared/Button';
import Badge from '../../components/shared/Badge';

interface TerminalLine {
  id: number;
  type: 'input' | 'output' | 'error' | 'system';
  content: string;
  timestamp: Date;
}

const SAMPLE_COMMANDS: Record<string, string | string[] | (() => string)> = {
  help: `Available commands:
  help          Show this help message
  clear         Clear the terminal
  ls            List files in current directory
  pwd           Print working directory
  date          Show current date and time
  whoami        Show current user
  echo [text]   Print text to terminal
  uptime        Show system uptime
  df            Show disk usage
  ps            Show running processes
  neofetch      Show system info
  npm --version Show npm version
  node -v       Show node version`,
  pwd: '/home/user/projects/super-dashboard',
  whoami: 'developer',
  date: () => new Date().toString(),
  uptime: 'up 14 days, 3 hours, 42 minutes',
  'npm --version': '10.2.5',
  'node -v': 'v20.11.0',
  ls: `node_modules/  src/  public/  package.json  tsconfig.json  vite.config.ts
.gitignore  README.md  index.html`,
  df: `Filesystem      Size   Used  Avail Use%  Mounted on
/dev/sda1       500G   234G   266G  47%   /
/dev/sdb1       1.0T   723G   277G  73%   /data`,
  ps: `  PID TTY          TIME CMD
 1234 pts/0    00:00:02 node
 1235 pts/0    00:00:01 vite
 1236 pts/0    00:00:00 tsc
 1237 pts/0    00:00:05 esbuild`,
  neofetch: `        .-/+oossssoo+/-.               developer@dashboard
     \`:+ssssssssssssssssss+:\`           ------------------
   -+ssssssssssssssssssyyssss+-         OS: Ubuntu 22.04.3 LTS x86_64
 .ossssssssssssssssssdMMMNysssso.       Host: Cloud VM Instance
/ssssssssssshdmmNNmmyNMMMMhssssss/      Kernel: 5.15.0-91-generic
+ssssssssshmydMMMMMMMNddddyssssss+      Uptime: 14 days, 3 hours
/sssssssshNMMMyhhyyyyhmNMMMNhssss/      Packages: 1847 (dpkg)
.ssssssssdMMMNhssssssssshmNNhssss.      Shell: bash 5.1.16
+ssssssss+so++osshhhyyhddssssssss+      Terminal: xterm-256color
\\ssssssss/     \`....\`   +sssssss/       CPU: Intel Xeon @ 2.8GHz (4)
 +sssso+:.                .:+sss+       Memory: 8192MB / 16384MB
  \`:/+-.                    .-+/:\``,
};

export default function TerminalPage() {
  const [lines, setLines] = useState<TerminalLine[]>([
    { id: 0, type: 'system', content: 'Welcome to Super Dashboard Terminal', timestamp: new Date() },
    { id: 1, type: 'system', content: 'Type "help" for available commands', timestamp: new Date() },
  ]);
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const terminalRef = useRef<HTMLDivElement>(null);
  const idCounter = useRef(2);

  const scrollToBottom = useCallback(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [lines, scrollToBottom]);

  const executeCommand = useCallback((cmd: string) => {
    const trimmed = cmd.trim().toLowerCase();
    const id = idCounter.current++;

    // Add the command to output
    setLines(prev => [...prev, { id, type: 'input', content: `$ ${cmd}`, timestamp: new Date() }]);

    if (trimmed === 'clear') {
      setLines([{ id: idCounter.current++, type: 'system', content: 'Terminal cleared', timestamp: new Date() }]);
      return;
    }

    // Check for echo command
    if (trimmed.startsWith('echo ')) {
      const text = cmd.slice(5);
      setLines(prev => [...prev, { id: idCounter.current++, type: 'output', content: text, timestamp: new Date() }]);
      return;
    }

    // Check for known commands
    const response = SAMPLE_COMMANDS[trimmed];
    if (response) {
      const rawOutput = typeof response === 'function' ? response() : response;
      const output = Array.isArray(rawOutput) ? rawOutput.join('\n') : rawOutput;
      setLines(prev => [...prev, { id: idCounter.current++, type: 'output', content: output, timestamp: new Date() }]);
    } else if (trimmed) {
      setLines(prev => [...prev, {
        id: idCounter.current++,
        type: 'error',
        content: `Command not found: ${trimmed.split(' ')[0]}. Type "help" for available commands.`,
        timestamp: new Date()
      }]);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    setHistory(prev => [...prev, input]);
    setHistoryIndex(-1);
    executeCommand(input);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (history.length > 0) {
        const newIndex = historyIndex < history.length - 1 ? historyIndex + 1 : historyIndex;
        setHistoryIndex(newIndex);
        setInput(history[history.length - 1 - newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(history[history.length - 1 - newIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput('');
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      // Simple autocomplete for known commands
      const commands = Object.keys(SAMPLE_COMMANDS);
      const matches = commands.filter(c => c.startsWith(input.toLowerCase()));
      if (matches.length === 1) {
        setInput(matches[0]);
      }
    }
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  return (
    <div className="terminal-page">
      <PageHeader
        title="Terminal"
        breadcrumb={['Ops', 'Terminal']}
        actions={
          <>
            <Badge variant="success">Connected</Badge>
            <Button variant="ghost" size="sm" onClick={() => setLines([])}>Clear</Button>
            <Button size="sm" onClick={() => executeCommand('neofetch')}>System Info</Button>
          </>
        }
      />

      <div className="terminal-container" onClick={focusInput}>
        <div className="terminal-header">
          <div className="terminal-buttons">
            <span className="terminal-btn terminal-btn--close" />
            <span className="terminal-btn terminal-btn--minimize" />
            <span className="terminal-btn terminal-btn--maximize" />
          </div>
          <span className="terminal-title">bash - super-dashboard</span>
        </div>

        <div className="terminal-body" ref={terminalRef}>
          {lines.map(line => (
            <div key={line.id} className={`terminal-line terminal-line--${line.type}`}>
              <pre>{line.content}</pre>
            </div>
          ))}

          <form onSubmit={handleSubmit} className="terminal-input-line">
            <span className="terminal-prompt">$</span>
            <input
              ref={inputRef}
              type="text"
              className="terminal-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              spellCheck={false}
              autoComplete="off"
            />
          </form>
        </div>
      </div>

      <div className="terminal-footer">
        <span>Press Tab for autocomplete • Arrow Up/Down for history</span>
        <span>{history.length} commands in history</span>
      </div>
    </div>
  );
}
